import { datadogRum } from '@datadog/browser-rum';

import { getCreationSource } from './creation-source';

interface User {
  _id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  ownerOrganization?: {
    _id?: string;
    name?: string;
  };
  organizationId?: string;
  roles?: string[];
  permissions?: string[];
  oktaUserId?: string;
}

export const hasValidDatadogRumEnvironmentVariables = () => {
  if (!process.env.ENABLE_DATADOG_RUM) {
    return false;
  }

  if (
    !process.env.DATADOG_RUM_APPLICATION_ID ||
    !process.env.DATADOG_RUM_CLIENT_TOKEN
  ) {
    // eslint-disable-next-line no-console
    console.warn('Datadog RUM: Missing required environment variables');
    return false;
  }

  return true;
};

/**
 * Initialize Datadog RUM with the provided configuration
 */
export const initializeDatadogRum = () => {
  if (!hasValidDatadogRumEnvironmentVariables()) {
    return;
  }

  datadogRum.init({
    applicationId: process.env.DATADOG_RUM_APPLICATION_ID!,
    clientToken: process.env.DATADOG_RUM_CLIENT_TOKEN!,
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: 'datadoghq.com',
    service: 'marley-chat',
    // from app/src/helpers/pend/pendo.js
    env: process.env.MARLEY_ACTIVE_ACCOUNT,
    // Specify a version number to identify the deployed version of your application in Datadog
    version: process.env.DD_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100, // record 100% of sessions
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    enablePrivacyForActionName: true,
    defaultPrivacyLevel: 'allow', // allow by default and mask components using data-dd-privacy="hidden"
    // cookie first, fall back to localStorage when cookies are unavailable
    sessionPersistence: ['cookie', 'local-storage'],
  });
};

/**
 * Setup Datadog RUM user and context
 * @param user - The user object containing user information
 */
export const setupDatadogRumUserAndContext = (user: User): void => {
  if (!hasValidDatadogRumEnvironmentVariables()) {
    return;
  }
  // Set user identification for Datadog RUM
  if (user._id) {
    const userName =
      user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`
        : user.email;

    datadogRum.setUser({
      id: user._id,
      name: userName,
      email: user.email,
    });
  }

  // Set global context for filtering and analysis
  const globalContext = {
    organization_id: user.ownerOrganization?._id || user.organizationId,
    organization_name: user.ownerOrganization?.name,
    marley_active_account: process.env.MARLEY_ACTIVE_ACCOUNT,
    user_roles: user.roles,
    user_permissions: user.permissions,
    okta_user_id: user.oktaUserId,
    platform: getCreationSource(),
  };

  // Filter out undefined values
  const filteredContext = Object.fromEntries(
    Object.entries(globalContext).filter(([, value]) => value !== undefined),
  );

  datadogRum.setGlobalContext(filteredContext);
};

const datadogRumActions = {
  OKTA_LOGIN_SUCCESS: 'okta_login_success',
  OKTA_LOGIN_FAILURE: 'okta_login_failure',
  // Forced-logout investigation (HMB). These trace the full mobile logout path
  // so we can distinguish "~60 min access-token expiry" from "backgrounded N
  // minutes" and confirm whether the fixes hold.
  NATIVE_REFRESH_REQUESTED: 'native_auth_refresh_requested',
  NATIVE_REFRESH_SUCCEEDED: 'native_auth_refresh_succeeded',
  NATIVE_REFRESH_ERROR: 'native_auth_refresh_error',
  NATIVE_REFRESH_TIMEOUT: 'native_auth_refresh_timeout',
  NATIVE_SIGN_OUT_REQUESTED: 'native_auth_sign_out_requested',
  DEAUTH_401: 'auth_deauth_on_401',
  DEAUTH_401_RECOVERED: 'auth_deauth_401_recovered', // retry succeeded, logout avoided
  HEALTH_CHECK_TRANSIENT: 'auth_health_check_transient_error', // kept session, no logout
};

// Session start (ms epoch) so every auth event can report elapsed-since-login.
let authSessionStartedAt: number | null = null;

/**
 * Anchor the auth-telemetry session clock.
 *
 * @param atMs Explicit start time (ms epoch) — pass the ID token `auth_time`
 *   so elapsed-since-login reflects the real authentication moment (needed for
 *   native sessions, which never hit the web login widget). Omit to anchor at
 *   "now". Ignores non-finite input and never sets the clock in the future.
 */
export const markAuthSessionStart = (atMs?: number): void => {
  if (typeof atMs === 'number' && Number.isFinite(atMs) && atMs > 0) {
    authSessionStartedAt = Math.min(atMs, Date.now());
    return;
  }
  authSessionStartedAt = Date.now();
};

const elapsedSinceLoginSec = (): number =>
  authSessionStartedAt === null
    ? -1
    : Math.round((Date.now() - authSessionStartedAt) / 1000);

/**
 * Strip likely PII / secrets from a free-form string before it reaches Datadog.
 * Error text (native bridge / API retry) can embed emails, identifiers, or
 * token fragments. Order matters: email → JWT → generic token → long digit run.
 */
// Context keys that may carry arbitrary/free-form text (and thus PII). Only
// these are run through redactPii; structural fields are left untouched.
const FREE_TEXT_KEYS = ['error', 'reason', 'initial_error', 'message'];

export const redactPii = (value: string): string =>
  value
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[email]')
    .replace(
      /\b[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g,
      '[jwt]',
    )
    // Long run that ALSO contains a digit — real tokens have entropy, so this
    // catches them without mangling snake_case enum reasons (no digits).
    .replace(/\b(?=[A-Za-z0-9_-]*\d)[A-Za-z0-9_-]{16,}\b/g, '[token]')
    .replace(/\b\d{7,}\b/g, '[num]')
    .slice(0, 300);

/**
 * Record an auth-lifecycle event to Datadog RUM (and console as a fallback).
 * Best-effort: never throws — telemetry must not break the auth flow. Never
 * pass token values in `context` (PII/security).
 */
export const trackAuthEvent = (
  action: string,
  context: Record<string, unknown> = {},
): void => {
  const enriched: Record<string, unknown> = {
    elapsed_since_login_sec: elapsedSinceLoginSec(),
    is_mobile_webview: !!(
      typeof window !== 'undefined' &&
      (window.isLoadedInWKWebView || window.loadedFromMobile)
    ),
    ...context,
  };

  // Redact PII only from the free-text fields that can carry arbitrary error
  // text. Structural values (action names, phases, statuses) are known-safe and
  // must stay intact — blanket-redacting every value over-catches (e.g.
  // action_name "GET_USER_BY_OKTA_USER_ID" -> "[token]"), destroying the signal.
  for (const key of FREE_TEXT_KEYS) {
    if (typeof enriched[key] === 'string') {
      enriched[key] = redactPii(enriched[key] as string);
    }
  }

  try {
    // eslint-disable-next-line no-console
    console.log(`[AuthTelemetry] ${action}`, enriched);
    datadogRum?.addAction?.(action, enriched);
  } catch {
    // swallow — telemetry is never allowed to affect auth behavior
  }
};

export { datadogRum, datadogRumActions };
